import React from 'react';
import ParticlesBG from '../../Shared/ParticlesBG';
import '../../Assets/Stylesheets/Pages/Legal/Legal.scss';

export default function PrivacyPolicy() {
  const lastUpdated = "January 20, 2026";
  const companyName = "TRND";
  const companyEmail = "privacy@trnd.co";
  // TODO (counsel): replace with the full registered business address. A
  // jurisdiction placeholder is not sufficient under Quebec Law 25.
  const companyAddress = "Canada";
  // TODO (counsel): Law 25 requires a named Person in charge of the protection
  // of personal information. Fill in the actual name/title before launch.
  const privacyOfficer = "Privacy Officer / Responsable de la protection des renseignements personnels";

  return (
    <div className="legal-page">
      <ParticlesBG />
      <div className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: {lastUpdated}</p>

        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            Welcome to {companyName} ("we," "our," or "us"). We are committed to protecting your 
            personal information and your right to privacy. This Privacy Policy explains how we 
            collect, use, disclose, and safeguard your information when you visit our website 
            and use our services.
          </p>
          <p>
            Please read this privacy policy carefully. If you do not agree with the terms of this 
            privacy policy, please do not access the site or use our services.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information You Provide</h3>
          <p>We collect personal information that you voluntarily provide to us when you:</p>
          <ul>
            <li>Register for an account</li>
            <li>Subscribe to our services</li>
            <li>Make a purchase</li>
            <li>Contact us for support</li>
            <li>Sign up for our newsletter</li>
            <li>Use our AI marketing tools</li>
          </ul>
          <p>This information may include:</p>
          <ul>
            <li>Name and contact information (email address, phone number)</li>
            <li>Billing and payment information</li>
            <li>Company/business information</li>
            <li>Account credentials</li>
            <li>Marketing preferences</li>
          </ul>

          <h3>2.2 Information Automatically Collected</h3>
          <p>
            When you visit our website, we automatically collect certain information about your 
            device, including:
          </p>
          <ul>
            <li>IP address and location data</li>
            <li>Browser type and version</li>
            <li>Operating system</li>
            <li>Pages visited and time spent</li>
            <li>Referring website addresses</li>
            <li>Device identifiers</li>
          </ul>

          <h3>2.3 Information from Third Parties</h3>
          <p>We may receive information about you from third-party services, including:</p>
          <ul>
            <li>Social media platforms when you connect your accounts</li>
            <li>Payment processors when you make purchases</li>
            <li>Analytics providers</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect for various purposes, including:</p>
          <ul>
            <li>Providing and maintaining our services</li>
            <li>Processing transactions and sending related information</li>
            <li>Sending promotional communications (with your consent)</li>
            <li>Responding to your inquiries and support requests</li>
            <li>Analyzing usage to improve our services</li>
            <li>Personalizing your experience</li>
            <li>Detecting and preventing fraud</li>
            <li>Complying with legal obligations</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. AI-Generated Content and Data Processing</h2>
          <p>
            Our platform uses artificial intelligence to generate marketing content and provide 
            recommendations. When you use our AI tools:
          </p>
          <ul>
            <li>Your input data is processed by our AI systems and third-party AI providers (OpenAI)</li>
            <li>Generated content may be stored to improve our services</li>
            <li>We do not use your business-specific data to train general AI models</li>
            <li>You retain ownership of the content generated for your account</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. Third-Party Services</h2>
          <p>We share your information with the following third-party service providers:</p>
          
          <h3>5.1 Supabase</h3>
          <p>
            We use Supabase for authentication and database services. Your account information 
            and data are stored securely on Supabase's infrastructure.
          </p>
          
          <h3>5.2 Zoho</h3>
          <p>
            We use Zoho for billing, subscriptions, and CRM services. Your billing information 
            and customer data are processed through Zoho's platform.
          </p>
          
          <h3>5.3 Stripe</h3>
          <p>
            Payment processing is handled by Stripe (through Zoho). We do not store your complete 
            payment card details on our servers.
          </p>
          
          <h3>5.4 OpenAI</h3>
          <p>
            Our AI features are powered by OpenAI. When you use AI tools, your prompts and 
            business information may be sent to OpenAI for processing.
          </p>
          
          <h3>5.5 Vercel</h3>
          <p>
            Our website is hosted on Vercel. Server logs and performance data may be collected 
            by Vercel's infrastructure.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Data Retention</h2>
          <p>
            We retain your personal information for as long as necessary to provide our services 
            and fulfill the purposes outlined in this policy. Specifically:
          </p>
          <ul>
            <li>Account data: Retained until you delete your account</li>
            <li>Transaction records: Retained for 7 years for tax and legal purposes</li>
            <li>Marketing preferences: Until you unsubscribe</li>
            <li>AI-generated content: Retained until you delete it or close your account</li>
            <li>Log data: Typically retained for 90 days</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>7. Your Privacy Rights</h2>
          
          <h3>7.1 General Rights</h3>
          <p>Depending on your location, you may have the following rights:</p>
          <ul>
            <li><strong>Access:</strong> Request access to your personal data</li>
            <li><strong>Correction:</strong> Request correction of inaccurate data</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data</li>
            <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
            <li><strong>Objection:</strong> Object to processing of your personal data</li>
            <li><strong>Restriction:</strong> Request restriction of processing</li>
          </ul>

          <h3>7.2 GDPR Rights (European Users)</h3>
          <p>
            If you are a resident of the European Economic Area (EEA), you have additional rights 
            under the General Data Protection Regulation (GDPR), including:
          </p>
          <ul>
            <li>The right to withdraw consent at any time</li>
            <li>The right to lodge a complaint with a supervisory authority</li>
            <li>The right to know the legal basis for processing your data</li>
          </ul>

          <h3>7.3 CCPA Rights (California Residents)</h3>
          <p>
            If you are a California resident, you have rights under the California Consumer 
            Privacy Act (CCPA), including:
          </p>
          <ul>
            <li>The right to know what personal information is collected</li>
            <li>The right to know whether your personal information is sold or disclosed</li>
            <li>The right to opt-out of the sale of personal information</li>
            <li>The right to non-discrimination for exercising your privacy rights</li>
          </ul>
          <p>
            <strong>Note:</strong> We do not sell your personal information to third parties.
          </p>

          <h3>7.4 Canadian Privacy Rights (PIPEDA and Quebec Law 25)</h3>
          <p>
            {companyName} is a Canadian business and is subject to the Personal
            Information Protection and Electronic Documents Act (PIPEDA) and, for
            residents of Quebec, the Act respecting the protection of personal
            information in the private sector, as amended by Law 25. If you are in
            Canada you have the right to:
          </p>
          <ul>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate or incomplete information</li>
            <li>Withdraw your consent to the collection, use, or disclosure of your information, subject to legal or contractual restrictions</li>
            <li>Request that your personal information be de-indexed or deleted where the law permits</li>
            <li>Request a copy of the computerized personal information you provided, in a structured, commonly used technological format (data portability)</li>
            <li>Be informed when your information is used to render a decision based exclusively on automated processing</li>
            <li>Lodge a complaint with the Office of the Privacy Commissioner of Canada or, in Quebec, the Commission d'acces a l'information (CAI)</li>
          </ul>
          <p>
            We will notify you and the appropriate authorities of any confidentiality
            incident involving your personal information that presents a risk of serious
            injury, as required by applicable Canadian law.
          </p>
          <p>
            The person responsible for the protection of personal information can be
            reached at {companyEmail}.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to collect and store information. 
            For detailed information about our use of cookies, please see our{' '}
            <a href="/cookie-policy">Cookie Policy</a>.
          </p>
          <p>Types of cookies we use:</p>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
            <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
            <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
            <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>9. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your 
            personal information, including:
          </p>
          <ul>
            <li>Encryption of data in transit (HTTPS/TLS)</li>
            <li>Encryption of sensitive data at rest</li>
            <li>Regular security assessments</li>
            <li>Access controls and authentication</li>
            <li>Secure hosting infrastructure</li>
          </ul>
          <p>
            However, no method of transmission over the Internet or electronic storage is 100% 
            secure. While we strive to protect your personal information, we cannot guarantee 
            its absolute security.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your 
            country of residence. These countries may have different data protection laws. 
            We ensure appropriate safeguards are in place for such transfers, including:
          </p>
          <ul>
            <li>Standard contractual clauses approved by relevant authorities</li>
            <li>Compliance with applicable data transfer frameworks</li>
            <li>Ensuring third parties maintain adequate data protection standards</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>11. Children's Privacy</h2>
          <p>
            Our services are not intended for individuals under the age of 18. We do not 
            knowingly collect personal information from children. If we learn that we have 
            collected personal information from a child under 18, we will take steps to 
            delete that information as quickly as possible.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any 
            changes by posting the new Privacy Policy on this page and updating the "Last Updated" 
            date. We encourage you to review this Privacy Policy periodically.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or wish to exercise your privacy 
            rights, please contact us:
          </p>
          <ul className="contact-info">
            <li><strong>Person in charge of personal information protection:</strong> {privacyOfficer}</li>
            <li><strong>Email:</strong> {companyEmail}</li>
            <li><strong>Address:</strong> {companyAddress}</li>
          </ul>
          <p>
            For GDPR-related inquiries (EEA residents) or Canadian PIPEDA and Quebec
            Law 25 inquiries, you may contact the person named above at the email
            address provided.
          </p>
          <p>
            Une version francaise de la presente politique est disponible sur demande.
            A French-language version of this policy is available on request.
          </p>
        </section>
      </div>
    </div>
  );
}
